import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { useApiMutation, useApiQuery } from '@/hooks/new_base_hooks';

const baseUrl = (process.env.NEXT_PUBLIC_API_URL || '/api/').replace(/\/?$/, '/');
const CONNECTIONS_URL = `${baseUrl}data-ingestion/aws-connections/`;

export type AwsConnectionListItem = {
  id: number;
  sourceType: string;
  name: string;
  status: string;
  accountId?: string;
  accountIdVerified?: boolean;
  accountLabel?: string;
  awsAccountId: string;
  awsAccountIdVerified: boolean;
  awsRoleArn: string;
  stackName: string;
};

export type AwsConnectionListResponse = {
  results: AwsConnectionListItem[];
};

export type AwsConnectionStartResponse = {
  externalId: string;
  consoleUrl: string;
  stackName: string;
  expectedRoleName: string;
  status: string;
  dataSourceId?: number;
};

export type AwsConnectionStatusResponse = {
  externalId: string;
  status: string;
  statusMessage: string;
  accountId?: string;
  accountIdVerified?: boolean;
  accountLabel?: string;
  awsAccountId: string;
  awsAccountIdVerified: boolean;
  awsRoleArn: string;
  stackName: string;
  timestamps: {
    creatingConnectionConfigAt: string | null;
    awaitingCredentialsAt: string | null;
    accountReportedAt: string | null;
    readyAt: string | null;
    failedAt: string | null;
  };
};

export type ScanTeaserStatus = 'pending' | 'running' | 'completed' | 'failed' | null;

export type AwsConnectionTeaserWaste = {
  status: ScanTeaserStatus;
  count: number;
  monthlyCostUsd: number;
};

export type AwsConnectionTeaserCommitment = {
  status: ScanTeaserStatus;
  coveragePercent: number;
  eligibleMonthlyUsd: number;
  hasActiveCommitments: boolean;
};

export type AwsConnectionTeaserCloud = {
  status: ScanTeaserStatus;
  recommendedProvider: string | null;
  savingsPercent: number;
  vmCount: number;
  costAvailable: boolean;
};

export type AwsConnectionTeaserResponse = {
  externalId: string;
  claimed: boolean;
  connectionStatus?: string;
  currency?: string;
  waste?: AwsConnectionTeaserWaste;
  commitment?: AwsConnectionTeaserCommitment;
  cloud?: AwsConnectionTeaserCloud;
};

function isTerminalConnectionStatus(status?: string | null): boolean {
  return status === 'ready' || status === 'failed';
}

function isActiveScanStatus(status?: ScanTeaserStatus): boolean {
  return status === 'pending' || status === 'running';
}

function isTeaserAnalyzing(teaser?: AwsConnectionTeaserResponse): boolean {
  if (!teaser || teaser.claimed) return false;
  if (!isTerminalConnectionStatus(teaser.connectionStatus)) return true;
  if (teaser.connectionStatus === 'failed') return false;
  return (
    isActiveScanStatus(teaser.waste?.status) ||
    isActiveScanStatus(teaser.commitment?.status) ||
    isActiveScanStatus(teaser.cloud?.status) ||
    teaser.waste?.status == null ||
    teaser.commitment?.status == null ||
    teaser.cloud?.status == null
  );
}

export function useAwsConnections(enabled = true) {
  return useApiQuery<AwsConnectionListResponse>({
    queryKey: ['data-ingestion', 'aws-connections'],
    enabled,
  });
}

export function useStartAwsConnection() {
  return useApiMutation<Record<string, never>, AwsConnectionStartResponse>({
    listQueryKey: ['data-ingestion', 'aws-connections'],
    method: 'POST',
  });
}

export function useAwsConnectionStatus(externalId?: string | null) {
  return useQuery({
    queryKey: ['data-ingestion', 'aws-connections', externalId, 'status'],
    enabled: Boolean(externalId),
    queryFn: async (): Promise<AwsConnectionStatusResponse> => {
      const { data } = await axios.get<AwsConnectionStatusResponse>(
        `${CONNECTIONS_URL}${externalId}/status/`,
        { withCredentials: true },
      );
      return data;
    },
    refetchInterval: (query) =>
      isTerminalConnectionStatus(query.state.data?.status) ? false : 3000,
  });
}

export function useAwsConnectionTeaser(externalId?: string | null) {
  return useQuery({
    queryKey: ['data-ingestion', 'aws-connections', externalId, 'teaser'],
    enabled: Boolean(externalId),
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error) && error.response?.status === 404) return false;
      return failureCount < 2;
    },
    queryFn: async (): Promise<AwsConnectionTeaserResponse> => {
      const { data } = await axios.get<AwsConnectionTeaserResponse>(
        `${CONNECTIONS_URL}${externalId}/teaser/`,
        { withCredentials: true },
      );
      return data;
    },
    refetchInterval: (query) => {
      const teaser = query.state.data;
      if (!teaser || teaser.claimed) return false;
      if (!isTerminalConnectionStatus(teaser.connectionStatus)) return 3000;
      return isTeaserAnalyzing(teaser) ? 5000 : false;
    },
  });
}

export function useClaimAwsConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (externalId: string): Promise<AwsConnectionListItem> => {
      const { data } = await axios.post<AwsConnectionListItem>(
        `${CONNECTIONS_URL}${externalId}/claim/`,
        {},
        { withCredentials: true },
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['data-ingestion', 'aws-connections'],
      });
    },
  });
}
